import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  alertVisibilitySchema,
  type AlertVisibility,
} from '@kirin/api-client/contracts/alerts';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  ALERTS_VISIBILITY_URL_PARAM,
  readAlertVisibilityFromStorage,
} from '@/lib/alertVisibilityStorage';

export interface UseAlertVisibilityResult {
  /**
   * Currently selected visibility (the user's preference, possibly stale
   * relative to flags). Bind this to `<AlertVisibilitySelector value={...} />`
   * and the corresponding setter to its `onChange`.
   */
  visibility: AlertVisibility;
  setVisibility: (next: AlertVisibility) => void;
  /**
   * Visibility actually applied to the request — same as `visibility` when at
   * least one suppression flag is on, forced to `'active'` otherwise. The one
   * exception (KIR-3182): when the page was opened with an explicit
   * `?visibility=all` deep-link override, that wins over the flag-loading
   * `'active'` floor so notification links land on the All view immediately
   * (no empty-Active flash). Bind this to the request param + React Query key
   * so flag-off responses stay byte-identical to the pre-KIR-2512 default-list
   * shape regardless of any saved preference.
   */
  effectiveVisibility: AlertVisibility;
  /** `true` when at least one of `alert_muting` / `alert_ignore` is on. */
  anyEnabled: boolean;
}

/**
 * Reads the optional `?visibility=` URL override. Invalid values fall
 * through to the stored preference rather than corrupting state.
 */
function readUrlVisibilityOverride(
  searchParams: URLSearchParams
): AlertVisibility | null {
  const raw = searchParams.get(ALERTS_VISIBILITY_URL_PARAM);
  if (raw === null) return null;
  const parsed = alertVisibilitySchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Centralised state machine for the alerts toolbar visibility selector
 * (KIR-2512, KIR-2606 deep-link override).
 *
 * - Initial value: `?visibility=` URL param if valid, else
 *   `localStorage` (legacy migration in `readAlertVisibilityFromStorage`).
 * - URL override is stripped on the next tick (`replace: true`) and is
 *   NOT persisted to `localStorage` — the user's saved preference is only
 *   updated by the selector's `userInteractedRef` write path.
 * - `effectiveVisibility` collapses to `'active'` while flags are
 *   loading or both are off, so the request shape stays byte-identical
 *   to the pre-KIR-2512 default — EXCEPT when the page was opened with an
 *   explicit `?visibility=all` override (KIR-3182). Notification deep links
 *   (Go agent / IDE extension) carry `?visibility=all` to land suppressed
 *   alerts on the All view; on a cold tab the suppression flags often
 *   aren't cached yet, so without this carve-out the first request would
 *   fire as `'active'` (empty) and only flip once flags resolve — a
 *   one-frame empty flash. Honoring the override immediately removes it.
 *   The carve-out is deliberately scoped to `'all'` only: it is the sole
 *   value any product surface deep-links, `'active'` already equals the
 *   floor, and a hand-crafted `?visibility=suppressed` should not bypass
 *   the floor. Safe when flags are off: the backend gates suppression
 *   per-flag, so `visibility=all` with flags off returns the same active set.
 */
export function useAlertVisibility(scopeKey: string): UseAlertVisibilityResult {
  const { flags, isLoading: flagsLoading } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_MUTING,
    flagsmithFeatureFlags.ALERT_IGNORE,
  ]);
  const mutingEnabled =
    !flagsLoading && flags[flagsmithFeatureFlags.ALERT_MUTING] === true;
  const ignoreEnabled =
    !flagsLoading && flags[flagsmithFeatureFlags.ALERT_IGNORE] === true;
  const anyEnabled = mutingEnabled || ignoreEnabled;

  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy: URL override wins over storage on first render only.
  const [visibility, setVisibility] = useState<AlertVisibility>(
    () =>
      readUrlVisibilityOverride(searchParams) ??
      readAlertVisibilityFromStorage(scopeKey)
  );

  // Capture, once on mount, whether the page was opened with an explicit
  // `?visibility=all` override. The override is stripped from the URL
  // post-render (effect below), so we cannot re-read it later — but
  // `visibility` retains the override value until the user interacts. We use
  // this to let the override win over the flag-loading `'active'` floor
  // (KIR-3182). Scoped to `'all'` specifically: it is the only value
  // notification deep links emit, `'active'` already equals the floor, and a
  // hand-crafted `?visibility=suppressed` must not bypass the floor.
  const [hadAllVisibilityOverride] = useState<boolean>(
    () => readUrlVisibilityOverride(searchParams) === 'all'
  );

  // Strip the `?visibility=` param post-render so subsequent toggles
  // persist normally and copy/paste does not pin the override. Idempotent
  // — once gone, further runs hit the early return.
  useEffect(() => {
    if (searchParams.get(ALERTS_VISIBILITY_URL_PARAM) === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete(ALERTS_VISIBILITY_URL_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const effectiveVisibility: AlertVisibility =
    anyEnabled || hadAllVisibilityOverride ? visibility : 'active';

  return {
    visibility,
    setVisibility,
    effectiveVisibility,
    anyEnabled,
  };
}
